import { EDonorStatusCode, EDonorStatusStep } from '@/entities/donor/model'
import type { IDonorCardDetail } from '@/entities/donor/model'
import {
  useGetDonorCardDetailQuery,
  useGetDonorDetailQuery,
} from '@/entities/donor/api/get-donor.api'

/**
 * EXAMINATION_REQUIRED is not admitted, but the therapist's examination is
 * exactly what clears it — so the steps up to the exam (survey, exam form)
 * stay open. Anything after it (screening, lab verdict, collection) stays
 * locked: `is_admitted` is false, so nothing may admit the donor to donation.
 */
const PRE_EXAMINATION_STEPS = new Set<string>([
  EDonorStatusStep.REGISTRATION,
  EDonorStatusStep.EXAMINATION,
])

/**
 * A donor under a deferral (MEDICAL_DEFERRAL, DEFERRAL, or UNKNOWN when the
 * deferrals could not be read) gets a view-only card: every tab opens, but no
 * action can be taken from any of them. Until the card has loaded the status
 * is unknown, so it counts as read-only too.
 */
export function isDonorCardReadOnly(
  donorCardDetail: IDonorCardDetail | undefined,
): boolean {
  const status = donorCardDetail?.current_status
  if (status?.status_code === EDonorStatusCode.ADMITTED) return false
  if (status?.status_code === EDonorStatusCode.EXAMINATION_REQUIRED) {
    return !!status.step && !PRE_EXAMINATION_STEPS.has(status.step)
  }
  return true
}

/**
 * Every business status the visit is in. `active_steps` holds them all (a
 * reserve donor can be at Забор and Донация at once); before the backend sent
 * it, `step` was the only one.
 */
export function getActiveStepCodes(
  donorCardDetail: IDonorCardDetail | undefined,
): string[] {
  const status = donorCardDetail?.current_status
  if (!status) return []
  if (status.active_steps) return status.active_steps.map((item) => item.code)
  return status.step ? [status.step] : []
}

/** Whether `code` is one of the visit's current business statuses. */
export function hasActiveStep(
  donorCardDetail: IDonorCardDetail | undefined,
  code: string,
): boolean {
  return getActiveStepCodes(donorCardDetail).includes(code)
}

/**
 * Steps that have an action on the card. `SUITABLE`, `DEFERRED`,
 * `PROCESSING` and `EXPEDITION` are real values, but nothing in the card acts
 * on them.
 */
const CARD_ACTION_STEPS = new Set<string>([
  EDonorStatusStep.REGISTRATION,
  EDonorStatusStep.EXAMINATION,
  EDonorStatusStep.SCREENING,
  EDonorStatusStep.BLOOD_COLLECTION,
  EDonorStatusStep.DONATION,
  EDonorStatusStep.LABORATORY,
  EDonorStatusStep.UNDER_THERAPIST_REVIEW,
])

/**
 * No active status left (`step = null`, empty `active_steps`) means the visit
 * is finished and the backend rejects any write to it. Statuses the card has
 * no action for — or does not know — are treated the same way, so an
 * unexpected value can only ever hide actions. Until the card has loaded it
 * counts as closed too.
 */
export function isVisitClosed(
  donorCardDetail: IDonorCardDetail | undefined,
): boolean {
  return !getActiveStepCodes(donorCardDetail).some((code) =>
    CARD_ACTION_STEPS.has(code),
  )
}

/** {@link isDonorCardReadOnly} for a tab that only knows its visit. */
export function useIsDonorCardReadOnly(visitId: string): boolean {
  const { data: donorDetail } = useGetDonorDetailQuery(visitId)
  const { data: donorCard } = useGetDonorCardDetailQuery(
    donorDetail?.donor_id ?? '',
  )
  return isDonorCardReadOnly(donorCard)
}

/** {@link isVisitClosed} for a tab that only knows its visit. */
export function useIsVisitClosed(visitId: string): boolean {
  const { data: donorDetail } = useGetDonorDetailQuery(visitId)
  const { data: donorCard } = useGetDonorCardDetailQuery(
    donorDetail?.donor_id ?? '',
  )
  return isVisitClosed(donorCard)
}
